import { useCallback, useEffect, useState } from 'react'
import {
  goalReviewService,
  type GoalReviewPrompt,
  type GoalReviewRespondResult,
  type GoalReviewResponseCode,
} from '../services/goalReviewService'

export function useGoalReviewPrompts(pollIntervalMs = 60000) {
  const [prompts, setPrompts] = useState<GoalReviewPrompt[]>([])

  const refetch = useCallback(async () => {
    try {
      const { prompts: next } = await goalReviewService.getReviewPrompts('today')
      setPrompts(next)
    } catch {
      // non-fatal
    }
  }, [])

  useEffect(() => {
    void refetch()
    const iv = window.setInterval(() => void refetch(), pollIntervalMs)
    return () => window.clearInterval(iv)
  }, [pollIntervalMs, refetch])

  // Don't dedupe on a hook-lifetime ref (a re-raised prompt keeps its id and
  // would never re-mark). The backend already no-ops when not 'available'.
  const markShown = useCallback(async (promptId: string) => {
    await goalReviewService.markShown(promptId)
  }, [])

  const respond = useCallback(
    async (promptId: string, code: GoalReviewResponseCode): Promise<GoalReviewRespondResult> => {
      const result = await goalReviewService.respond(promptId, code)
      // Only remove the card when the backend actually accepted the response.
      if (result.success) {
        setPrompts(prev => prev.filter(p => p.id !== promptId))
      }
      return result
    },
    [],
  )

  return { prompts, refetch, markShown, respond }
}
